import { useState, useEffect, useRef } from 'react';
import { Row, Col, Card, Statistic, Button, Table, Empty } from 'antd';
import { 
  UserOutlined, 
  TeamOutlined, 
  AppstoreOutlined, 
  CheckCircleOutlined, 
  ClockCircleOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
  RiseOutlined,
  LeftOutlined,
  RightOutlined
} from '@ant-design/icons';
import * as echarts from 'echarts';
import screenfull from 'screenfull';
import api from '../api';

// 时段配置
const TIME_PERIODS = [
  { key: 'all', label: '全天', start: '00:00', end: '23:59' },
  { key: 'morning', label: '早签 (7:30-8:30)', start: '07:30', end: '08:30' },
  { key: 'afternoon', label: '午签 (13:00-14:00)', start: '13:00', end: '14:00' },
  { key: 'evening', label: '晚签 (18:30-19:30)', start: '18:30', end: '19:30' }
];

const DashboardPage = () => {
  const [stats, setStats] = useState({
    totalStudents: 0,
    totalTeachers: 0,
    totalClasses: 0,
    activeSessions: 0,
    todaySessions: 0,
    weeklyTrend: [],
    classAttendanceRank: [],
    todaySignedStudents: 0,
    todayTotalStudents: 0,
    recentRecords: []
  });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [currentPeriodIndex, setCurrentPeriodIndex] = useState(0);
  const [periodRankData, setPeriodRankData] = useState([]);
  const [trendPeriodIndex, setTrendPeriodIndex] = useState(0);
  const [periodTrendData, setPeriodTrendData] = useState([]);
  const dashboardRef = useRef(null);
  
  const trendChartRef = useRef(null);
  const rankChartRef = useRef(null);

  const fetchStats = async () => {
    try {
      const response = await api.get('/statistics/dashboard');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard stats', error);
    }
  };

  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, 30000); // 30秒刷新一次
    return () => clearInterval(interval);
  }, []);

  // 获取指定时段的排行数据
  const fetchPeriodRank = async (periodKey) => {
    try {
      const period = TIME_PERIODS.find(p => p.key === periodKey);
      const response = await api.get('/statistics/class-rank-by-period', {
        params: { startTime: period.start, endTime: period.end }
      });
      setPeriodRankData(response.data || []);
    } catch (error) {
      console.error('Failed to fetch period rank', error);
      setPeriodRankData([]);
    }
  };

  // 切换时段时获取数据
  useEffect(() => {
    fetchPeriodRank(TIME_PERIODS[currentPeriodIndex].key);
  }, [currentPeriodIndex]);

  // 获取指定时段的趋势数据
  const fetchPeriodTrend = async (periodKey) => {
    try {
      const period = TIME_PERIODS.find(p => p.key === periodKey);
      const response = await api.get('/statistics/weekly-trend-by-period', {
        params: { startTime: period.start, endTime: period.end }
      });
      setPeriodTrendData(response.data || []);
    } catch (error) {
      console.error('Failed to fetch period trend', error);
      setPeriodTrendData([]);
    }
  };

  // 切换趋势时段时获取数据
  useEffect(() => {
    fetchPeriodTrend(TIME_PERIODS[trendPeriodIndex].key);
  }, [trendPeriodIndex]);

  const handlePrevTrendPeriod = () => {
    setTrendPeriodIndex(prev => (prev - 1 + TIME_PERIODS.length) % TIME_PERIODS.length);
  };

  const handleNextTrendPeriod = () => {
    setTrendPeriodIndex(prev => (prev + 1) % TIME_PERIODS.length);
  };

  const handlePrevPeriod = () => {
    setCurrentPeriodIndex(prev => (prev - 1 + TIME_PERIODS.length) % TIME_PERIODS.length);
  };

  const handleNextPeriod = () => {
    setCurrentPeriodIndex(prev => (prev + 1) % TIME_PERIODS.length);
  };

  const toggleFullscreen = () => {
    if (screenfull.isEnabled && dashboardRef.current) {
      screenfull.toggle(dashboardRef.current);
    }
  };

  useEffect(() => {
    if (screenfull.isEnabled) {
      const handleChange = () => setIsFullscreen(screenfull.isFullscreen);
      screenfull.on('change', handleChange);
      return () => screenfull.off('change', handleChange);
    }
  }, []);

  // 签到趋势图表 - 近7日趋势
  useEffect(() => {
    if (!trendChartRef.current || !stats.weeklyTrend?.length) return;
    
    const chart = echarts.init(trendChartRef.current);
    const dates = stats.weeklyTrend.map(d => d.date);
    const sessions = stats.weeklyTrend.map(d => d.sessions);
    const records = stats.weeklyTrend.map(d => d.records);

    chart.setOption({
      tooltip: { 
        trigger: 'axis',
        axisPointer: { type: 'cross' }
      },
      legend: { data: ['签到场次', '签到人次'], bottom: 0 },
      grid: { left: '3%', right: '4%', bottom: '15%', top: '10%', containLabel: true },
      xAxis: {
        type: 'category',
        data: dates,
        axisLine: { lineStyle: { color: '#d9d9d9' } },
        axisLabel: { color: '#666' }
      },
      yAxis: [
        {
          type: 'value',
          name: '场次',
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: { lineStyle: { type: 'dashed', color: '#eee' } }
        },
        {
          type: 'value',
          name: '人次',
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: { show: false }
        }
      ],
      series: [
        {
          name: '签到场次',
          type: 'bar',
          data: sessions,
          barWidth: '40%',
          itemStyle: { color: '#1890ff', borderRadius: [4, 4, 0, 0] }
        },
        {
          name: '签到人次',
          type: 'line',
          yAxisIndex: 1,
          data: records,
          smooth: true,
          symbol: 'circle',
          symbolSize: 8,
          itemStyle: { color: '#52c41a' },
          lineStyle: { width: 3 },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: 'rgba(82, 196, 26, 0.3)' },
                { offset: 1, color: 'rgba(82, 196, 26, 0.05)' }
              ]
            }
          }
        }
      ]
    });

    const handleResize = () => chart.resize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      chart.dispose();
    };
  }, [stats.weeklyTrend]);

  // 班级出勤率排行图表
  useEffect(() => {
    if (!rankChartRef.current) return;
    
    const chart = echarts.init(rankChartRef.current);
    
    if (!periodRankData?.length) {
      chart.clear();
      return;
    }
    
    const classNames = periodRankData.map(d => d.className).reverse();
    const rates = periodRankData.map(d => d.attendanceRate).reverse();

    chart.setOption({
      tooltip: { 
        trigger: 'axis',
        formatter: '{b}: {c}%'
      },
      grid: { left: '3%', right: '15%', bottom: '3%', top: '3%', containLabel: true },
      xAxis: {
        type: 'value',
        max: 100,
        axisLabel: { formatter: '{value}%' },
        splitLine: { lineStyle: { type: 'dashed', color: '#eee' } }
      },
      yAxis: {
        type: 'category',
        data: classNames,
        axisLine: { lineStyle: { color: '#d9d9d9' } },
        axisTick: { show: false }
      },
      series: [{
        type: 'bar',
        data: rates.map((value, index) => ({
          value,
          itemStyle: {
            color: value >= 90 ? '#52c41a' : value >= 70 ? '#faad14' : '#ff4d4f',
            borderRadius: [0, 4, 4, 0]
          }
        })),
        barWidth: '60%',
        label: {
          show: true,
          position: 'right',
          formatter: '{c}%',
          color: '#666'
        }
      }]
    });

    const handleResize = () => chart.resize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      chart.dispose();
    };
  }, [periodRankData]);

  // 各班级签到统计表格列
  const classAttendanceColumns = [
    { title: '班级', dataIndex: 'className', key: 'className', width: 80 },
    { title: '应签', dataIndex: 'expectedCount', key: 'expectedCount', width: 60, align: 'center' },
    { title: '扫码', dataIndex: 'scanCount', key: 'scanCount', width: 60, align: 'center',
      render: (val) => <span style={{ color: '#52c41a' }}>{val}</span>
    },
    { title: '补签', dataIndex: 'manualCount', key: 'manualCount', width: 60, align: 'center',
      render: (val) => <span style={{ color: '#faad14' }}>{val}</span>
    },
    { title: '未签', dataIndex: 'unsignedCount', key: 'unsignedCount', width: 60, align: 'center',
      render: (val) => <span style={{ color: '#595959' }}>{val}</span>
    },
    { title: '迟到', dataIndex: 'lateCount', key: 'lateCount', width: 60, align: 'center',
      render: (val) => <span style={{ color: '#1890ff' }}>{val}</span>
    },
    { title: '请假', dataIndex: 'leaveCount', key: 'leaveCount', width: 60, align: 'center',
      render: (val) => <span style={{ color: '#722ed1' }}>{val}</span>
    },
    { title: '旷课', dataIndex: 'absentCount', key: 'absentCount', width: 60, align: 'center',
      render: (val) => <span style={{ color: '#ff4d4f' }}>{val}</span>
    },
    { title: '其他', dataIndex: 'otherCount', key: 'otherCount', width: 60, align: 'center',
      render: (val) => <span style={{ color: '#8c8c8c' }}>{val}</span>
    }
  ];

  // 计算今日出勤率
  const todayRate = stats.todayTotalStudents > 0 
    ? ((stats.todaySignedStudents / stats.todayTotalStudents) * 100).toFixed(1) 
    : 0;

  return (
    <div 
      ref={dashboardRef} 
      style={{ 
        padding: isFullscreen ? 32 : 24, 
        background: isFullscreen ? '#f0f2f5' : 'transparent', 
        minHeight: isFullscreen ? '100vh' : 'auto', 
        overflow: 'auto' 
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h2 style={{ margin: 0, fontSize: isFullscreen ? 28 : 24, fontWeight: 600, color: '#262626' }}>数据概览</h2>
        <Button 
          icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />} 
          onClick={toggleFullscreen}
        >
          {isFullscreen ? '退出全屏' : '全屏模式'}
        </Button>
      </div>

      {/* 统计卡片 */}
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={4}>
          <Card variant="filled" hoverable>
            <Statistic 
              title="总学员数" 
              value={stats.totalStudents} 
              prefix={<UserOutlined style={{ color: '#36cfc9' }} />}
              valueStyle={{ fontSize: isFullscreen ? 28 : 24 }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={4}>
          <Card variant="filled" hoverable>
            <Statistic 
              title="总教师数" 
              value={stats.totalTeachers} 
              prefix={<TeamOutlined style={{ color: '#597ef7' }} />}
              valueStyle={{ fontSize: isFullscreen ? 28 : 24 }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={4}>
          <Card variant="filled" hoverable>
            <Statistic 
              title="班级总数" 
              value={stats.totalClasses} 
              prefix={<AppstoreOutlined style={{ color: '#ffc53d' }} />}
              valueStyle={{ fontSize: isFullscreen ? 28 : 24 }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={4}>
          <Card variant="filled" hoverable>
            <Statistic 
              title="今日签到场次" 
              value={stats.todaySessions} 
              prefix={<CheckCircleOutlined style={{ color: '#73d13d' }} />}
              valueStyle={{ fontSize: isFullscreen ? 28 : 24 }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={4}>
          <Card variant="filled" hoverable>
            <Statistic 
              title="今日出勤率" 
              value={todayRate}
              suffix="%"
              prefix={<RiseOutlined style={{ color: '#1890ff' }} />}
              valueStyle={{ fontSize: isFullscreen ? 28 : 24, color: todayRate >= 90 ? '#52c41a' : todayRate >= 70 ? '#faad14' : '#ff4d4f' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={4}>
          <Card variant="filled" hoverable style={{ background: stats.activeSessions > 0 ? '#fff1f0' : undefined }}>
            <Statistic 
              title="进行中签到" 
              value={stats.activeSessions} 
              prefix={<ClockCircleOutlined spin={stats.activeSessions > 0} style={{ color: '#ff4d4f' }} />}
              valueStyle={{ fontSize: isFullscreen ? 28 : 24, color: stats.activeSessions > 0 ? '#ff4d4f' : undefined }}
            />
          </Card>
        </Col>
      </Row>

      {/* 图表区域 */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={14}>
          <Card 
            title="近7日签到趋势" 
            variant="filled" 
            hoverable 
            styles={{ body: { height: isFullscreen ? 350 : 300 } }}
          >
            {stats.weeklyTrend?.length > 0 ? (
              <div ref={trendChartRef} style={{ height: '100%', width: '100%' }} />
            ) : (
              <Empty description="暂无数据" style={{ marginTop: 80 }} />
            )}
          </Card>
        </Col>
        <Col xs={24} lg={10}>
          <Card 
            title={
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Button 
                  type="text" 
                  icon={<LeftOutlined />} 
                  onClick={handlePrevPeriod}
                  size="small"
                />
                <span style={{ flex: 1, textAlign: 'center' }}>
                  班级出勤率排行 ({TIME_PERIODS[currentPeriodIndex].label})
                </span>
                <Button 
                  type="text" 
                  icon={<RightOutlined />} 
                  onClick={handleNextPeriod}
                  size="small"
                />
              </div>
            }
            variant="filled" 
            hoverable 
            styles={{ body: { height: isFullscreen ? 350 : 300 } }}
          >
            {periodRankData?.length > 0 ? (
              <div ref={rankChartRef} style={{ height: '100%', width: '100%' }} />
            ) : (
              <Empty description="该时段暂无数据" style={{ marginTop: 80 }} />
            )}
          </Card>
        </Col>
      </Row>

      {/* 各班级签到统计 */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col span={24}>
          <Card 
            title={
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Button 
                  type="text" 
                  icon={<LeftOutlined />} 
                  onClick={handlePrevTrendPeriod}
                  size="small"
                />
                <span style={{ flex: 1, textAlign: 'center' }}>
                  各班级签到统计 ({TIME_PERIODS[trendPeriodIndex].label})
                </span>
                <Button 
                  type="text" 
                  icon={<RightOutlined />} 
                  onClick={handleNextTrendPeriod}
                  size="small"
                />
              </div>
            }
            variant="filled" 
            hoverable
          >
            <Table
              columns={classAttendanceColumns}
              dataSource={periodTrendData || []}
              rowKey={(record) => record.className}
              pagination={false}
              size="small"
              locale={{ emptyText: <Empty description="该时段暂无数据" /> }}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default DashboardPage;
